import { useMemo, useState } from 'react';
import type { CheckResult, DomainStatus } from '@domaintriage/shared';

interface ResultsTableProps {
  results: CheckResult[];
  onSelectDomain: (domain: string) => void;
  shortlist: Set<string>;
  onToggleShortlist: (domain: string) => void;
  onBackToInput: () => void;
  truncated: boolean;
  truncatedCount: number;
}

type SortField = 'domain' | 'status';
type SortDir = 'asc' | 'desc';
type FilterValue = 'all' | DomainStatus;

const STATUS_ORDER: Record<DomainStatus, number> = {
  available: 0,
  unknown: 1,
  registered: 2,
};

function StatusBadge({ status }: { status: DomainStatus }) {
  const config: Record<DomainStatus, { bg: string; text: string; label: string }> = {
    available: {
      bg: 'bg-green-100 dark:bg-green-900/40',
      text: 'text-green-800 dark:text-green-300',
      label: 'Available',
    },
    registered: {
      bg: 'bg-red-100 dark:bg-red-900/40',
      text: 'text-red-800 dark:text-red-300',
      label: 'Registered',
    },
    unknown: {
      bg: 'bg-amber-100 dark:bg-amber-900/40',
      text: 'text-amber-800 dark:text-amber-300',
      label: 'Unknown',
    },
  };

  const c = config[status];

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${c.bg} ${c.text}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          status === 'available'
            ? 'bg-green-500'
            : status === 'registered'
              ? 'bg-red-500'
              : 'bg-amber-500'
        }`}
        aria-hidden="true"
      />
      {c.label}
    </span>
  );
}

function formatTime(isoString: string): string {
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return isoString;
  }
}

export function ResultsTable({
  results,
  onSelectDomain,
  shortlist,
  onToggleShortlist,
  onBackToInput,
  truncated,
  truncatedCount,
}: ResultsTableProps) {
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [filter, setFilter] = useState<FilterValue>('all');

  const filtered = useMemo(() => {
    if (filter === 'all') return results;
    return results.filter((r) => r.status === filter);
  }, [results, filter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'domain') {
        cmp = a.domain.localeCompare(b.domain);
      } else {
        cmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) {
      return (
        <svg className="ml-1 inline h-3 w-3 opacity-30" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M6 1l4 4H2z" fill="currentColor" />
          <path d="M6 11l4-4H2z" fill="currentColor" />
        </svg>
      );
    }
    return (
      <svg className="ml-1 inline h-3 w-3" viewBox="0 0 12 12" aria-hidden="true">
        {sortDir === 'asc' ? (
          <path d="M6 2l4 5H2z" fill="currentColor" />
        ) : (
          <path d="M6 10l4-5H2z" fill="currentColor" />
        )}
      </svg>
    );
  }

  const availableCount = results.filter((r) => r.status === 'available').length;
  const registeredCount = results.filter((r) => r.status === 'registered').length;
  const unknownCount = results.filter((r) => r.status === 'unknown').length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Results</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {results.length} domain{results.length === 1 ? '' : 's'} checked
            {availableCount > 0 && (
              <span className="ml-2 text-green-600 dark:text-green-400">
                {availableCount} available
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter */}
          <label htmlFor="status-filter" className="sr-only">
            Filter by status
          </label>
          <select
            id="status-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterValue)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
          >
            <option value="all">All ({results.length})</option>
            <option value="available">Available ({availableCount})</option>
            <option value="registered">Registered ({registeredCount})</option>
            <option value="unknown">Unknown ({unknownCount})</option>
          </select>

          {/* Back */}
          <button
            type="button"
            onClick={onBackToInput}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Check Again
          </button>
        </div>
      </div>

      {/* Truncation notice */}
      {truncated && (
        <div
          role="status"
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          Results were truncated. {truncatedCount} domain{truncatedCount === 1 ? ' was' : 's were'}{' '}
          not checked due to the per-request limit.
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden overflow-hidden rounded-lg border border-gray-200 shadow-sm sm:block dark:border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  type="button"
                  onClick={() => handleSort('domain')}
                  className="font-semibold text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Domain
                  <SortIcon field="domain" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  type="button"
                  onClick={() => handleSort('status')}
                  className="font-semibold text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Status
                  <SortIcon field="status" />
                </button>
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-400">
                Checked At
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600 dark:text-gray-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
            {sorted.map((result) => (
              <tr
                key={result.domain}
                className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onClick={() => onSelectDomain(result.domain)}
                tabIndex={0}
                role="button"
                aria-label={`View details for ${result.domain}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectDomain(result.domain);
                  }
                }}
              >
                <td className="px-4 py-3">
                  <span className="font-mono font-medium text-gray-900 dark:text-white">
                    {result.domain}
                  </span>
                  {result.cached && (
                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">(cached)</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={result.status} />
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                  {formatTime(result.checkedAt)}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleShortlist(result.domain);
                    }}
                    aria-label={
                      shortlist.has(result.domain)
                        ? `Remove ${result.domain} from shortlist`
                        : `Add ${result.domain} to shortlist`
                    }
                    aria-pressed={shortlist.has(result.domain)}
                    className="rounded-md p-1.5 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill={shortlist.has(result.domain) ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      strokeWidth={2}
                      className={`h-5 w-5 ${
                        shortlist.has(result.domain)
                          ? 'text-yellow-500'
                          : 'text-gray-400 dark:text-gray-500'
                      }`}
                      aria-hidden="true"
                    >
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="space-y-3 sm:hidden">
        {sorted.map((result) => (
          <div
            key={result.domain}
            className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
            onClick={() => onSelectDomain(result.domain)}
            tabIndex={0}
            role="button"
            aria-label={`View details for ${result.domain}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectDomain(result.domain);
              }
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono font-medium text-gray-900 dark:text-white">
                  {result.domain}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <StatusBadge status={result.status} />
                  {result.cached && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">(cached)</span>
                  )}
                </div>
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Checked at {formatTime(result.checkedAt)}
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleShortlist(result.domain);
                }}
                aria-label={
                  shortlist.has(result.domain)
                    ? `Remove ${result.domain} from shortlist`
                    : `Add ${result.domain} to shortlist`
                }
                aria-pressed={shortlist.has(result.domain)}
                className="shrink-0 rounded-md p-1.5 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill={shortlist.has(result.domain) ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth={2}
                  className={`h-5 w-5 ${
                    shortlist.has(result.domain)
                      ? 'text-yellow-500'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                  aria-hidden="true"
                >
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {sorted.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No domains match the current filter.
          </p>
        </div>
      )}
    </div>
  );
}
